import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import CheatSheet from '../../components/admin/CheatSheet';
import { Settings, Shield, Key, Database, Mail, Send, Eye, Clock, Loader2, CheckCircle, XCircle, Plus, X } from 'lucide-react';
import { apiClient } from '../../api/client';

const AdminSettings = () => {
  const [digestConfig, setDigestConfig] = useState(null);
  const [digestLoading, setDigestLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [digestHistory, setDigestHistory] = useState([]);
  const [integrations, setIntegrations] = useState([]);

  const loadConfig = useCallback(async () => {
    try {
      const [config, history, integrationsData] = await Promise.all([
        apiClient.request('/api/admin/digest/config'),
        apiClient.request('/api/admin/digest/history?limit=5').catch(() => ({ history: [] })),
        apiClient.request('/api/admin/system/integrations').catch(() => ({ integrations: [] })),
      ]);
      setDigestConfig(config);
      setDigestHistory(history.history || []);
      setIntegrations(integrationsData.integrations || []);
    } catch (e) { console.error('Failed to load digest config:', e); }
    setDigestLoading(false);
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await apiClient.request('/api/admin/digest/config', {
        method: 'POST',
        body: JSON.stringify({
          frequency: digestConfig.frequency,
          recipients: digestConfig.recipients || [],
          daily_hour_utc: digestConfig.daily_hour_utc || 7,
          weekly_day: digestConfig.weekly_day || 'Monday',
        }),
      });
      setSendResult({ type: 'success', message: 'Digest settings saved' });
    } catch (e) {
      setSendResult({ type: 'error', message: 'Failed to save settings' });
    }
    setSaving(false);
    setTimeout(() => setSendResult(null), 3000);
  };

  const sendNow = async (period) => {
    setSending(true);
    setSendResult(null);
    try {
      const res = await apiClient.request('/api/admin/digest/send', {
        method: 'POST',
        body: JSON.stringify({ period }),
      });
      if (res.status === 'error') {
        setSendResult({ type: 'error', message: res.message });
      } else {
        setSendResult({ type: 'success', message: `${period} digest queued for ${res.recipients?.length || 0} recipient(s)` });
        loadConfig();
      }
    } catch (e) {
      setSendResult({ type: 'error', message: 'Failed to send digest' });
    }
    setSending(false);
    setTimeout(() => setSendResult(null), 5000);
  };

  const previewDigest = async (period) => {
    setPreviewLoading(true);
    try {
      const res = await apiClient.request(`/api/admin/digest/preview?period=${period}`);
      setPreviewHtml(res.html);
    } catch (e) { console.error('Preview failed:', e); }
    setPreviewLoading(false);
  };

  const addRecipient = () => {
    if (newEmail && newEmail.includes('@') && !digestConfig.recipients.includes(newEmail)) {
      setDigestConfig({ ...digestConfig, recipients: [...(digestConfig.recipients || []), newEmail] });
      setNewEmail('');
    }
  };

  const removeRecipient = (email) => {
    setDigestConfig({ ...digestConfig, recipients: (digestConfig.recipients || []).filter(e => e !== email) });
  };

  return (
    <AdminLayout pageTitle="Settings">
      <CheatSheet pageId="settings" />
      <div className="max-w-3xl space-y-6">
        {/* Security */}
        <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
          <div className="flex items-center gap-2 mb-4"><Shield size={18} style={{ color: '#F5831F' }} /><h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Security & Access</h3></div>
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: '#F9F9F9' }}>
              <div><p className="font-medium" style={{ color: '#333333' }}>Admin Authentication</p><p className="text-xs" style={{ color: '#44403A' }}>Uses existing JWT role-based auth</p></div>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Active</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: '#F9F9F9' }}>
              <div><p className="font-medium" style={{ color: '#333333' }}>RBAC Enforcement</p><p className="text-xs" style={{ color: '#44403A' }}>Admin role required for all endpoints</p></div>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Enforced</span>
            </div>
          </div>
        </div>

        {/* API Connections */}
        <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
          <div className="flex items-center gap-2 mb-4"><Key size={18} style={{ color: '#7B4FD8' }} /><h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>API Connections</h3></div>
          <div className="space-y-3 text-sm" data-testid="api-connections-list">
            {integrations.length > 0 ? integrations.map((api, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: '#F9F9F9' }}>
                <div><p className="font-medium" style={{ color: '#333333' }}>{api.name}</p><p className="text-xs" style={{ color: '#44403A' }}>{api.desc}</p></div>
                <span className="px-3 py-1 rounded-full text-xs font-medium"
                  style={{
                    backgroundColor: api.color === 'green' ? '#DCFCE7' : api.color === 'amber' ? '#FEF3C7' : '#FEE2E2',
                    color: api.color === 'green' ? '#166534' : api.color === 'amber' ? '#92400E' : '#991B1B',
                  }}
                  data-testid={`integration-status-${i}`}
                >{api.status}</span>
              </div>
            )) : (
              <p className="text-xs" style={{ color: '#6B6257' }}>Loading integration status...</p>
            )}
          </div>
        </div>

        {/* Email Digest Config */}
        <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0', borderTop: '3px solid #F5831F' }} data-testid="digest-config">
          <div className="flex items-center gap-2 mb-4">
            <Mail size={18} style={{ color: '#F5831F' }} />
            <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Admin Email Digest</h3>
          </div>

          {digestLoading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" style={{ color: '#F5831F' }} /></div>
          ) : digestConfig ? (
            <div className="space-y-4">
              {/* Status */}
              {sendResult && (
                <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${sendResult.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-testid="digest-status">
                  {sendResult.type === 'success' ? <CheckCircle size={16} /> : <XCircle size={16} />}
                  {sendResult.message}
                </div>
              )}

              {/* Frequency */}
              <div>
                <label className="text-xs font-medium mb-1.5 block" style={{ color: '#666' }}>Frequency</label>
                <div className="flex gap-2">
                  {['daily', 'weekly', 'both', 'off'].map(f => (
                    <button
                      key={f}
                      onClick={() => setDigestConfig({ ...digestConfig, frequency: f })}
                      className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
                      style={{
                        backgroundColor: digestConfig.frequency === f ? '#F5831F' : '#F5F5F5',
                        color: digestConfig.frequency === f ? 'white' : '#666',
                      }}
                      data-testid={`digest-freq-${f}`}
                    >
                      {f.charAt(0).toUpperCase() + f.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Schedule */}
              {digestConfig.frequency !== 'off' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium mb-1.5 block" style={{ color: '#666' }}>Daily Send Hour (UTC)</label>
                    <select
                      value={digestConfig.daily_hour_utc || 7}
                      onChange={e => setDigestConfig({ ...digestConfig, daily_hour_utc: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E0E0E0' }}
                      data-testid="digest-hour-select"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{String(i).padStart(2, '0')}:00 UTC</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium mb-1.5 block" style={{ color: '#666' }}>Weekly Send Day</label>
                    <select
                      value={digestConfig.weekly_day || 'Monday'}
                      onChange={e => setDigestConfig({ ...digestConfig, weekly_day: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E0E0E0' }}
                      data-testid="digest-day-select"
                    >
                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Recipients */}
              <div>
                <label className="text-xs font-medium mb-1.5 block" style={{ color: '#666' }}>Recipients</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="email"
                    value={newEmail}
                    onChange={e => setNewEmail(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addRecipient()}
                    placeholder="admin@example.com"
                    className="flex-1 px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E0E0E0' }}
                    data-testid="digest-email-input"
                  />
                  <button onClick={addRecipient} className="px-3 py-2 rounded-lg text-white text-sm" style={{ backgroundColor: '#F5831F' }} data-testid="digest-add-email">
                    <Plus size={16} />
                  </button>
                </div>
                {(digestConfig.recipients || []).length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {digestConfig.recipients.map((email, i) => (
                      <span key={i} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#FFF7ED', color: '#C2410C' }} data-testid={`digest-recipient-${i}`}>
                        {email}
                        <button onClick={() => removeRecipient(email)} className="hover:opacity-70"><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs" style={{ color: '#6B6257' }}>No recipients added yet. Add email addresses above.</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button onClick={saveConfig} disabled={saving} className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium" style={{ backgroundColor: '#F5831F' }} data-testid="digest-save-btn">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Settings size={14} />}
                  Save Settings
                </button>
                <button onClick={() => sendNow('daily')} disabled={sending} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border" style={{ borderColor: '#F5831F', color: '#F5831F' }} data-testid="digest-send-daily-btn">
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  Send Daily Now
                </button>
                <button onClick={() => sendNow('weekly')} disabled={sending} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border" style={{ borderColor: '#7B4FD8', color: '#7B4FD8' }} data-testid="digest-send-weekly-btn">
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  Send Weekly Now
                </button>
                <button onClick={() => previewDigest('daily')} disabled={previewLoading} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border" style={{ borderColor: '#E0E0E0', color: '#666' }} data-testid="digest-preview-btn">
                  {previewLoading ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                  Preview
                </button>
              </div>

              {/* Digest History */}
              {digestHistory.length > 0 && (
                <div className="mt-4">
                  <label className="text-xs font-medium mb-2 block" style={{ color: '#666' }}>Recent Sends</label>
                  <div className="space-y-2">
                    {digestHistory.map((h, i) => (
                      <div key={i} className="flex items-center justify-between p-2.5 rounded-lg text-xs" style={{ backgroundColor: '#F9F9F9' }}>
                        <div className="flex items-center gap-2">
                          {h.result?.success ? <CheckCircle size={12} className="text-green-600" /> : <XCircle size={12} className="text-red-500" />}
                          <span style={{ color: '#333' }}>{h.to}</span>
                          <span className="px-1.5 py-0.5 rounded" style={{ backgroundColor: '#F0F0F0', color: '#666' }}>{h.period}</span>
                        </div>
                        <span style={{ color: '#6B6257' }}>{h.sent_at?.slice(0, 16).replace('T', ' ')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Preview Modal */}
        {previewHtml && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setPreviewHtml(null)}>
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()} data-testid="digest-preview-modal">
              <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: '#E0E0E0' }}>
                <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Digest Preview</h3>
                <button onClick={() => setPreviewHtml(null)} className="p-1 rounded hover:bg-gray-100"><X size={18} /></button>
              </div>
              <div className="overflow-y-auto" style={{ maxHeight: 'calc(85vh - 48px)' }}>
                <iframe srcDoc={previewHtml} title="Digest Preview" className="w-full border-0" style={{ height: '600px' }} />
              </div>
            </div>
          </div>
        )}

        {/* Database */}
        <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
          <div className="flex items-center gap-2 mb-4"><Database size={18} style={{ color: '#2563EB' }} /><h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Database</h3></div>
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: '#F9F9F9' }}>
              <div><p className="font-medium" style={{ color: '#333333' }}>MongoDB</p><p className="text-xs" style={{ color: '#44403A' }}>Primary data store — hibff_v3</p></div>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Healthy</span>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
